import React, { ReactElement, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { buildSidebarData } from "./SidebarData";
import "./sideBar.scss";
import logo from "../../assets/logo/GII.png";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { getLocalStorageData } from "../../utils/LocalStorageService";


type SidebarProps = {
  isOpen: boolean;
};

export type SidebarItem = {
  title: string;
  path: string;
  icon: ReactElement;
  cName: string;
  roles?: string[];
  dynamicSubNav?: boolean;
};

export type SidebarGroup = {
  title: string;
  path: string;
  icon: ReactElement;
  cName: string;
  roles?: string[];
  subNav?: SidebarItem[];
};

type SidebarElement = SidebarItem | SidebarGroup;

const Sidebar: React.FC<SidebarProps> = ({ isOpen }) => {
  const navigate = useNavigate();
  const localStorageObj: any = getLocalStorageData("localUser");
  const userType = (localStorageObj?.userType || "").toLowerCase();

  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const [sidebarData, setSidebarData] = useState<SidebarElement[]>([]);

  // Filter sidebar data based on user roles
  useEffect(() => {
    const rawData = buildSidebarData();
    const filteredData = rawData
      .filter(item => {
        return !item.roles || item.roles.some(role => role.toLowerCase() === userType);
      })
      .map((item: any) => {
        if ('subNav' in item) {
          return {
            ...item,
            subNav: item.subNav.filter((subItem: any) =>
              !subItem.roles || subItem.roles.some((role: any) => role.toLowerCase() === userType)
            ),
          };
        }
        return item;
      });

    // setSidebarData(filteredData);
    setSidebarData(filteredData.length > 0 ? filteredData : rawData);
  }, [userType]);

  const toggleMenu = (title: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const renderSidebarItems = (items: SidebarElement[], level = 0) => {
    return items.map((item: any, index) => {
      const hasSubNav = 'subNav' in item && item.subNav && item.subNav.length > 0;
      const isExpanded = expandedMenus[item.title];
      const uniqueKey = `${item.title}-${level}-${index}`;

      if (hasSubNav) {
        return (
          <div key={uniqueKey}>
            <div
              className={`nav-link px-3 py-${level === 0 ? 3 : 2} d-flex align-items-center gap-2 mb-${level === 0 ? 2 : 1}`}
              onClick={() => toggleMenu(item.title)}
              style={{ cursor: 'pointer' }}
            >
              <span className="sidebar-icon">{item.icon}</span>
              {isOpen && <span className="sidebar-text">{item.title}</span>}
              {isOpen && (
                <span className="ms-auto">
                  {isExpanded ? <IoIosArrowUp /> : <IoIosArrowDown />}
                </span>
              )}
            </div>
            {isExpanded && (
              <div className={`ps-${level + 3}`}>
                {renderSidebarItems(item.subNav, level + 1)}
              </div>
            )}
          </div>
        );
      }

      // Regular menu item remains the same
      return (
        <NavLink
          to={item.path}
          key={uniqueKey}
          className={({ isActive }) =>
            `nav-link px-3 py-${level === 0 ? 3 : 2} d-flex align-items-center gap-2 mb-${level === 0 ? 2 : 1} ${isActive ? 'active' : ''
            }`
          }
          end
        >
          <span className="sidebar-icon">{item.icon}</span>
          {isOpen && <span className="sidebar-text">{item.title}</span>}
        </NavLink>
      );
    });
  };

  return (
    <>
      {/* Desktop Sidebar */}
      {/* <div className={`sidebar ${isOpen ? 'open' : 'closed'} vh-100 shadow-sm d-none d-lg-block ms-3 mt-3`}> */}
      <div className={`sidebar ${isOpen ? 'open' : 'closed'} shadow-sm d-none d-lg-block`}>
        <nav className="nav flex-column pt-4">{renderSidebarItems(sidebarData)}</nav>
      </div>

      {/* Mobile Offcanvas */}
      <div
        className="offcanvas offcanvas-start d-lg-none"
        tabIndex={-1}
        id="offcanvasSidebar"
        aria-labelledby="offcanvasSidebarLabel"
      >
        <div className="offcanvas-header justify-content-between align-items-center px-3 py-2 border-bottom">
          <div className="d-flex align-items-center">
            <img src={logo} alt="Logo" style={{ height: 50, width: 50 }} />
          </div>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          />
        </div>

        <div className="offcanvas-body p-0 d-flex flex-column justify-content-between">
          <nav className="nav flex-column mobile-sidebar-nav pt-2">
            {renderSidebarItems(sidebarData)}
          </nav>
          <div className="text-center py-3 border-top small text-muted">
            Need Help? <NavLink to="/contact" className="text-decoration-none">Contact Us</NavLink>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;